import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getVisaDemands } from '../../../../api/endpoints/demands';
import { VISA_APPROVAL_STATUS_LABELS, type VisaDemand } from '../../../../api/types/demands';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { VisaDemandDialog } from '../VisaDemandDialog';

const STATUS_COLOR: Record<number, 'default' | 'success' | 'error'> = {
  0: 'default',
  1: 'success',
  2: 'error',
};

/** See BusDemandPage.tsx header comment for why this uses a plain MUI Table instead of
 *  @mui/x-data-grid (that package's typings don't parse under this project's pinned TS 4.9.5). */
export default function VisaDemandPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<VisaDemand | null>(null);

  const query = useQuery({
    queryKey: ['demands', 'visa'],
    queryFn: () => getVisaDemands(),
  });

  const rows: VisaDemand[] = query.data?.visaDemands ?? [];

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Vize Talepleri</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Talep
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Yolcu</TableCell>
              <TableCell>Ülke</TableCell>
              <TableCell>Amaç</TableCell>
              <TableCell>Gidiş Tarihi</TableCell>
              <TableCell>Dönüş Tarihi</TableCell>
              <TableCell>Personel ID</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı talep bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{[row.passengerName, row.passengerSurname].filter(Boolean).join(' ') || '—'}</TableCell>
                <TableCell>{row.country ?? '—'}</TableCell>
                <TableCell>{row.travelPurpose ?? '—'}</TableCell>
                <TableCell>{row.depDate ?? '—'}</TableCell>
                <TableCell>{row.retDate ?? '—'}</TableCell>
                <TableCell>{row.employeeID ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLOR[row.isApproved ?? 0] ?? 'default'}
                    label={VISA_APPROVAL_STATUS_LABELS[row.isApproved ?? 0] ?? String(row.isApproved)}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <VisaDemandDialog open={createOpen} demand={null} onClose={() => setCreateOpen(false)} />
      <VisaDemandDialog open={!!editing} demand={editing} onClose={() => setEditing(null)} />
    </Box>
  );
}
